import { useState } from "react";

import { Divider, List, ListItemButton, ListItemText } from "@mui/material";

import { Collapse } from "components/Collapse";
import {
  generateMenuAriaControlsProps,
  generateSubMenuIdProps,
} from "components/Menu/accessibility.helpers";
import { MenuNodeType } from "components/Menu/model";
import { ParentNodeIcon } from "components/ParentNodeIcon";
import { useAutoFocus } from "hooks/useAutoFocus";
import { useAvoTracking } from "hooks/useAvoTracking";

import { SubMenuChildNodes } from "../SubMenuChildNodes/SubMenuChildNodes";
import { sharedStyles } from "../shared.styles";

import { menuExpansionNodeStyles as styles } from "./MenuExpansionNode.styles";

export const MenuExpansionNode = ({
  node,
  isMobile,
  focus,
  containerLinkText,
  expansionLinkText,
}: {
  node: MenuNodeType.Expansion;
  isMobile: boolean;
  focus: boolean;
  containerLinkText?: string;
  expansionLinkText?: string;
}) => {
  const { trackMenuClick } = useAvoTracking();
  const [open, setOpen] = useState(false);
  const expansionAnalyticsTag = node.analyticsTag;
  const listItemButtonRef = useAutoFocus<HTMLDivElement>(focus);

  return (
    <>
      <ListItemButton
        ref={listItemButtonRef}
        data-testid="menu-expansion-node"
        onClick={() => {
          trackMenuClick({
            containerLinkText,
            expansionLinkText,
            name: expansionAnalyticsTag,
          });
          setOpen((open) => !open);
        }}
        aria-expanded={open}
        {...generateMenuAriaControlsProps({
          analyticsTag: node.analyticsTag,
          containerLinkText,
          targetElementShown: open,
        })}
      >
        <ListItemText css={sharedStyles.listItemHeading}>
          {node.titleKey}
        </ListItemText>
        <ParentNodeIcon closedDirection="down" open={open} />
      </ListItemButton>
      <Collapse open={open}>
        <List
          component="nav"
          aria-live="polite"
          {...generateSubMenuIdProps({
            analyticsTag: node.analyticsTag,
            containerLinkText,
          })}
        >
          <SubMenuChildNodes
            childNodes={node.childNodes}
            isMobile={isMobile}
            expansionLinkText={expansionAnalyticsTag}
            focus={false}
            containerLinkText={containerLinkText}
          />
        </List>
      </Collapse>
      <Divider css={!node.fullWidthDivider && styles.dividerMargins} />
    </>
  );
};
